import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalization } from '../../localization/LocalizationContext';
import {
  LOCAL_PROFILES,
  normalizeLocalParam,
  resolveWorkspaceCurrency,
} from '../../localization/locales';
import { useWorkspace } from '../WorkspaceContext';

/**
 * Resolved workspace currency for the active locale.
 * Honors ?locale=gh|ng|ke|us (also ?local= / ?country=) for preview verification.
 * Fallbacks: business country → business phone dial → stored currency → detected geo.
 */
export const useWorkspaceCurrency = () => {
  const { business } = useWorkspace();
  const { local, profile, detectedFrom } = useLocalization();
  const [searchParams] = useSearchParams();

  const paramLocal = normalizeLocalParam(
    searchParams.get('local') || searchParams.get('locale') || searchParams.get('country')
  );
  const hasLocaleOverride = Boolean(paramLocal) || detectedFrom === 'query';
  const activeLocal = paramLocal || local;

  const currency = useMemo(
    () =>
      resolveWorkspaceCurrency({
        business,
        localCode: activeLocal,
        hasLocaleOverride,
      }),
    [business, activeLocal, hasLocaleOverride]
  );

  const currencyMeta = LOCAL_PROFILES[activeLocal]?.currency || {
    code: currency,
    symbol: currency,
  };

  return {
    currency,
    currencyMeta,
    local: activeLocal,
    profile,
    hasLocaleOverride,
    detectedFrom: hasLocaleOverride ? 'query' : detectedFrom,
  };
};

export default useWorkspaceCurrency;
